import firebase from "firebase/app";
import "firebase/functions";
import chunk from "lodash/chunk";
import firestore from "src/apiService/firestore";
import { MaximumBatchSize } from "src/config/firestore";
import type { Inventory } from "src/interfaces/inventory.interface";

export type PendingMatchType = "title" | "sku" | "none";

export type PendingInventory = Inventory & {
  timestamp?: number;
  download_timestamp?: number;
  integrationId?: string;
  matchType?: PendingMatchType;
  /** Representative match for "Remove from Import". */
  matchedInventoryId?: string;
  matchedItemTitle?: string;
  matchedListDate?: unknown;
  matchedQuantity?: number;
};

export type RefreshDuplicateMatchesResult = {
  inventoryCount: number;
  pendingScanned: number;
  updated: number;
  hasMatches: boolean;
  /** First checker screen. null means there is nothing to match. */
  matchType: "title" | "sku" | null;
  titleMatches: number;
  skuMatches: number;
  noneCount: number;
  earliestTimestamp: number | null;
  /** Server time captured before the duplicate scan. */
  reviewStartedAt: number;
};

export const PendingInventoryChangedCode = "PENDING_INVENTORY_CHANGED";

/** True only for the backend's stale-edit conflict, not network/aborted errors. */
export function isPendingInventoryChangedError(error: unknown): boolean {
  const details = (error as { details?: { code?: string } } | null)?.details;
  return details?.code === PendingInventoryChangedCode;
}

export async function ingestMappedPendingInventoryFile(args: {
  filename: string;
  import_platform: string;
  integrationId?: string;
  timestamp?: number;
}): Promise<{
  written: number;
  updated: number;
  skipped: number;
  unchanged: number;
}> {
  const f = firebase
    .functions()
    .httpsCallable("pendingInventory-ingestMappedFile");
  return (await f(args)).data;
}

export async function getPendingInventoryCount(): Promise<number> {
  const f = firebase.functions().httpsCallable("pendingInventory-getCount");
  const data = (await f({})).data as { count: number };
  return data.count ?? 0;
}

export async function refreshPendingDuplicateMatches(): Promise<RefreshDuplicateMatchesResult> {
  const f = firebase
    .functions()
    .httpsCallable("pendingInventory-refreshDuplicateMatches");
  return (await f({})).data as RefreshDuplicateMatchesResult;
}

export async function promotePendingInventory(
  ids: string[],
  reviewStartedAt: number
): Promise<{ imported: number; skipped: number; changed: string[] }> {
  const f = firebase.functions().httpsCallable("pendingInventory-promote");
  const data = (await f({ ids, reviewStartedAt })).data as {
    imported?: number;
    skipped?: number;
    changed?: string[];
  };
  return {
    imported: data.imported ?? 0,
    skipped: data.skipped ?? 0,
    changed: data.changed ?? [],
  };
}

export async function updatePendingInventoryDocument(
  id: string,
  patch: Record<string, unknown>,
  reviewStartedAt: number
): Promise<{ updated: boolean }> {
  const f = firebase.functions().httpsCallable("pendingInventory-updateItem");
  const data = (await f({ id, patch, reviewStartedAt })).data as {
    updated?: boolean;
  };
  return { updated: data.updated !== false };
}

/** Existence check for the notification badge (limit 1). */
export async function hasPendingInventory(uid: string): Promise<boolean> {
  const snap = await firestore()
    .collection("Pending_Inventory")
    .where("user", "==", uid)
    .limit(1)
    .get();
  return !snap.empty;
}

export async function fetchPendingInventoryPage(args: {
  uid: string;
  pageSize: number;
  matchType?: PendingMatchType;
  /** When set, only rows with this exact timestamp (Review All off). */
  timestampEquals?: number;
  startAfterTimestamp?: number;
  startAfterId?: string;
}): Promise<{ items: PendingInventory[]; hasMore: boolean }> {
  const limitTo = args.pageSize + 1;

  let query: firebase.firestore.Query = firestore()
    .collection("Pending_Inventory")
    .where("user", "==", args.uid);

  if (args.matchType) {
    query = query.where("matchType", "==", args.matchType);
  }

  if (args.timestampEquals != null) {
    query = query.where("timestamp", "==", args.timestampEquals);
  }

  query = query
    .orderBy("timestamp", "asc")
    .orderBy(firebase.firestore.FieldPath.documentId(), "asc")
    .limit(limitTo);

  if (
    args.startAfterTimestamp != null &&
    args.startAfterId != null &&
    args.startAfterId !== ""
  ) {
    query = query.startAfter(args.startAfterTimestamp, args.startAfterId);
  }

  // Prevent the limit(1) existence query from satisfying this page out of cache.
  const snap = await query.get({ source: "server" });
  const docs = snap.docs;
  const hasMore = docs.length > args.pageSize;
  const pageDocs = hasMore ? docs.slice(0, args.pageSize) : docs;
  const items = pageDocs.map((doc) => {
    const data = doc.data() as PendingInventory;
    return { ...data, id: doc.id };
  });
  return { items, hasMore };
}

/** Move pending rows into the import queue (clear duplicate match). */
export async function clearPendingInventoryMatches(
  ids: string[]
): Promise<void> {
  if (!ids.length) return;
  const db = firestore();
  for (const c of chunk(ids, MaximumBatchSize)) {
    const batch = db.batch();
    for (const id of c) {
      batch.update(db.collection("Pending_Inventory").doc(id), {
        matchType: "none",
        matchedInventoryId: firebase.firestore.FieldValue.delete(),
        matchedItemTitle: firebase.firestore.FieldValue.delete(),
        matchedListDate: firebase.firestore.FieldValue.delete(),
        matchedQuantity: firebase.firestore.FieldValue.delete(),
      });
    }
    await batch.commit();
  }
}

export async function deletePendingInventoryDocuments(
  ids: string[]
): Promise<void> {
  if (!ids.length) return;
  const db = firestore();
  for (const c of chunk(ids, MaximumBatchSize)) {
    const batch = db.batch();
    for (const id of c) {
      batch.delete(db.collection("Pending_Inventory").doc(id));
    }
    await batch.commit();
  }
}

/** After direct/initial Inventory writes — drop matching pending review rows. */
export async function deletePendingInventoryByListingIds(
  uid: string,
  listingIds: string[]
): Promise<void> {
  const ids = [...new Set(listingIds.map(String).filter(Boolean))];
  if (!ids.length) return;
  const db = firestore();
  const toDelete: string[] = [];
  for (const c of chunk(ids, 10)) {
    const snap = await db
      .collection("Pending_Inventory")
      .where("user", "==", uid)
      .where("listing_id", "in", c)
      .get();
    for (const doc of snap.docs) toDelete.push(doc.id);
  }
  await deletePendingInventoryDocuments(toDelete);
}
